'use client';

import React, { useEffect, useRef } from 'react';

const phases = [
  {
    phase: 'Discover',
    duration: 'Weeks 1–2',
    items: [
      'Planning maturity assessment',
      'Stakeholder interviews',
      'Planning pain point analysis',
      'Planning architecture design review',
    ],
  },
  {
    phase: 'Prepare',
    duration: 'Weeks 3–4',
    items: [
      'Data landscape readiness assessment',
      'Planning process blueprint',
      'Modeling strategy document',
      'Governance structure definition',
    ],
  },
  {
    phase: 'Explore',
    duration: 'Weeks 5–8',
    items: [
      'Planning design workshops',
      'Driver identification sessions',
      'Planning dimension design',
      'Prototype model development',
    ],
  },
  {
    phase: 'Realize',
    duration: 'Weeks 9–16',
    items: [
      'Full model build',
      'Data integration configuration',
      'Planning logic development',
      'Testing cycles and user validation',
    ],
  },
  {
    phase: 'Deploy',
    duration: 'Weeks 17–20',
    items: [
      'Production environment setup',
      'Security role configuration',
      'Performance tuning',
      'End-user training program',
    ],
  },
  {
    phase: 'Run',
    duration: 'Ongoing',
    items: [
      'Planning operations support',
      'Model improvements and enhancements',
      'Forecast optimisation',
      'Continuous planning evolution',
    ],
  },
];

export default function ImplementationMethodology() {
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const section = sectionRef.current;
    if (!section) return;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.querySelectorAll('.reveal-on-scroll').forEach((el) => {
              (el as HTMLElement).classList.add('is-visible');
            });
          }
        });
      },
      { threshold: 0.03 }
    );
    observer.observe(section);
    return () => observer.disconnect();
  }, []);

  return (
    <section ref={sectionRef} className="py-20 px-6 border-b border-border bg-secondary">
      <div className="max-w-container mx-auto">
        <div className="mb-12 reveal-on-scroll">
          <div className="mono-label mb-3">SAP Activate Aligned</div>
          <h2
            className="font-heading font-semibold text-foreground"
            style={{ fontSize: 'clamp(1.75rem, 3vw, 2.5rem)', letterSpacing: '-0.025em' }}
          >
            SAC Planning Implementation Methodology
          </h2>
          <p className="mt-3 text-sm text-muted max-w-xl leading-relaxed font-body">
            Adapted from SAP Activate to address the specific requirements of SAC Planning programs.
            Each phase has defined outputs and decision gates.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {phases.map((p, i) => (
            <div
              key={p.phase}
              className={`reveal-on-scroll reveal-delay-${Math.min(i + 1, 6)} method-step bg-white border border-border p-6`}
              style={{ borderRadius: '4px' }}
            >
              <div className="flex items-start justify-between mb-4">
                <h3 className="font-heading font-semibold text-foreground text-base">{p.phase}</h3>
                <span className="mono-label">{p.duration}</span>
              </div>
              <ul className="flex flex-col gap-2">
                {p.items.map((item) => (
                  <li key={item} className="flex gap-2 text-sm font-body text-muted">
                    <span className="shrink-0 mt-1 w-1 h-1 rounded-full bg-border-strong inline-block" />
                    <span className="leading-relaxed">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}